import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { trackEvent } from '../utils/analytics';
import { buildDetectionQuery } from '../utils/marketOverride';
import { buildGoogleAuthUrl } from '../utils/googleAuthUrl';
import { ComplianceBlock } from './auth/ComplianceBlock';
import { OAuthButtons } from './auth/OAuthButtons';
import { WhatsAppOtpPanel } from './auth/WhatsAppOtpPanel';
import { EmailPasswordForm } from './auth/EmailPasswordForm';
import { RolePicker, RolePickerBackLink, ROLE_CARDS } from './auth/RolePicker';
import { LastAuthCard } from './auth/LastAuthCard';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const dashboardForRole = (role) => {
  // `investor` is a buyer subtype — same UI, different framing — so it
  // lands on the buyer dashboard. Every other role gets its own.
  const map = {
    admin: '/admin', seller: '/seller', buyer: '/buyer', agent: '/agent',
    professional: '/professional', tenant: '/tenant', investor: '/buyer',
  };
  return map[role] || '/buyer';
};

const UnifiedAuthPanel = ({ mode = 'register' }) => {
  const navigate = useNavigate();
  const { login, register, checkAuth } = useAuth();

  const [compliance, setCompliance] = useState(null);   // {country, framework, ...}
  const [agreed, setAgreed] = useState(false);

  // Default open in both login AND register — discoverability is more
  // valuable than the marginal vertical-space saving from a collapsed
  // accordion. Users were treating the hidden email panel as "no email
  // option exists" and bouncing.
  const [emailOpen, setEmailOpen] = useState(true);
  const [waOpen, setWaOpen] = useState(false);

  // Email form
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [role, setRole] = useState('buyer');
  const [emailLoading, setEmailLoading] = useState(false);

  // Two-step register flow: first pick role visually, then pick auth method.
  // Login skips the role step entirely.
  const [step, setStep] = useState(mode === 'register' ? 'role' : 'auth');

  // WhatsApp form
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [otpStage, setOtpStage] = useState('phone');     // 'phone' | 'code' | 'role'
  const [waLoading, setWaLoading] = useState(false);
  const [waCountdown, setWaCountdown] = useState(0);

  const [googleLoading, setGoogleLoading] = useState(false);
  const [lastAuth, setLastAuth] = useState(null);   // smart "Continue as..." hint

  // Read last-auth hint from localStorage (non-sensitive: email/name/picture/method)
  useEffect(() => {
    try {
      const raw = localStorage.getItem('shopperden_last_auth');
      if (!raw) return;
      const parsed = JSON.parse(raw);
      // Hide hint after 30 days
      if (parsed?.last_at && (Date.now() - new Date(parsed.last_at).getTime()) > 30 * 24 * 3600 * 1000) {
        localStorage.removeItem('shopperden_last_auth');
        return;
      }
      setLastAuth(parsed);
    } catch (_e) { /* ignore */ }
  }, []);

  const clearLastAuth = () => {
    try { localStorage.removeItem('shopperden_last_auth'); } catch (_e) { /* ignore */ }
    setLastAuth(null);
  };

  // Auto-detect country/regime from server. The frontend bundles its
  // browser timezone + any admin `?test=IN` override into the query
  // string — see utils/marketOverride.js.
  useEffect(() => {
    const url = `${API_URL}/api/compliance/me${buildDetectionQuery()}`;
    axios.get(url).then(r => setCompliance(r.data)).catch(() => {
      setCompliance({
        country_code: 'UK', country_name: 'United Kingdom',
        legal: { regime: 'UK CPRs + UK-GDPR', disclaimer: '' },
      });
    });
  }, []);

  // OTP countdown
  useEffect(() => {
    if (waCountdown <= 0) return;
    const t = setTimeout(() => setWaCountdown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [waCountdown]);

  const fw = compliance || {};
  const cc = fw.country_code || 'UK';
  const countryName = fw.country_name || 'United Kingdom';
  const regime = fw.legal?.regime || '';
  const disclaimer = fw.legal?.disclaimer || '';

  const consentMeta = useCallback(() => ({
    consent_country: cc,
    consent_regime: regime,
    consent_at: new Date().toISOString(),
    role,
  }), [cc, regime, role]);

  const flashConsentBlock = () => {
    const el = document.querySelector('[data-testid="compliance-block"]');
    if (!el) return;
    try { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (_e) { /* ignore */ }
    el.classList.add('animate-pulse');
    const orig = el.style.transition;
    el.style.transition = 'transform 80ms ease-in-out';
    let x = 0;
    const seq = [-6, 6, -4, 4, 0];
    const tick = () => {
      el.style.transform = `translateX(${seq[x]}px)`;
      x++;
      if (x < seq.length) setTimeout(tick, 80);
      else { el.style.transform = ''; el.style.transition = orig; }
    };
    tick();
    setTimeout(() => el.classList.remove('animate-pulse'), 1600);
  };

  const requireConsent = () => {
    if (!agreed) {
      flashConsentBlock();
      toast.error(`Please tick the ${countryName} compliance box above to continue.`);
      return false;
    }
    return true;
  };

  // ──────────── Google ────────────
  const handleGoogle = useCallback(() => {
    if (!agreed) {
      flashConsentBlock();
      toast.error(`Please tick the ${countryName} compliance box above to continue.`);
      return;
    }
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    setGoogleLoading(true);
    trackEvent('auth_google_clicked', '/login', { country: cc });
    try {
      sessionStorage.setItem('shopperden_consent', JSON.stringify({
        ...consentMeta(), country: cc,
      }));
    } catch (_e) { /* ignore */ }
    window.location.href = buildGoogleAuthUrl(redirectUrl);
  }, [agreed, cc, consentMeta, countryName]);

  // ──────────── WhatsApp OTP ────────────
  const requestOtp = async () => {
    if (!requireConsent()) return;
    if (!phone || phone.length < 8) return toast.error('Enter a valid phone number with country code (e.g. +447950523316)');
    setWaLoading(true);
    trackEvent('auth_whatsapp_request_otp', '/login', { country: cc });
    try {
      await axios.post(`${API_URL}/api/auth/whatsapp/request-otp`, { phone });
      setOtpStage('code');
      setWaCountdown(900);
      toast.success('Code sent on WhatsApp. Check your messages.');
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Could not send code. Please try Google or email.';
      toast.error(msg);
      trackEvent('registration_error', '/login', { reason: 'whatsapp_otp_send_failed', detail: String(msg).slice(0, 200) });
    } finally {
      setWaLoading(false);
    }
  };

  const verifyOtp = async () => {
    if (!otp || otp.length < 4) return toast.error('Enter the 4-digit code');
    setWaLoading(true);
    try {
      const r = await axios.post(`${API_URL}/api/auth/whatsapp/verify-otp`, {
        phone, code: otp, role, country: cc, ...consentMeta(),
      });
      if (r.data?.token) {
        sessionStorage.setItem('shopperden_access_token', btoa(r.data.token));
        try {
          localStorage.setItem('shopperden_last_auth', JSON.stringify({
            method: 'whatsapp', phone: r.data.phone || phone,
            country: r.data.country || cc, role: r.data.role || role,
            last_at: new Date().toISOString(),
          }));
        } catch (_e) { /* ignore */ }
        toast.success('Welcome to Shopperden!');
        await checkAuth();
        navigate(dashboardForRole(r.data.role));
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Invalid or expired code';
      toast.error(msg);
      trackEvent('registration_error', '/login', { reason: 'whatsapp_otp_verify_failed', detail: String(msg).slice(0, 200) });
    } finally {
      setWaLoading(false);
    }
  };

  // ──────────── Email + Password ────────────
  const handleEmail = async (e) => {
    e.preventDefault();
    if (!requireConsent()) return;
    if (!email || !password) return toast.error('Email and password required');
    if (password.length < 6) return toast.error('Password must be at least 6 characters');
    setEmailLoading(true);
    trackEvent('registration_attempt', '/login', { role, mode });
    try {
      if (mode === 'login') {
        const res = await login(email, password);
        if (res.success) {
          try {
            localStorage.setItem('shopperden_last_auth', JSON.stringify({
              method: 'email', email, role: res.role || '',
              last_at: new Date().toISOString(),
            }));
          } catch (_e) { /* ignore */ }
          navigate(dashboardForRole(res.role));
        } else toast.error(res.error || 'Login failed');
      } else {
        const res = await register(email, password, '', { role, country: cc, ...consentMeta() });
        if (res.success) {
          trackEvent('registration_complete', '/register', { role, auth_method: 'email_password' });
          try {
            localStorage.setItem('shopperden_last_auth', JSON.stringify({
              method: 'email', email, role: res.role || role,
              last_at: new Date().toISOString(),
            }));
          } catch (_e) { /* ignore */ }
          toast.success('Welcome to Shopperden!');
          navigate(dashboardForRole(res.role));
        } else {
          toast.error(res.error || 'Registration failed');
        }
      }
    } finally {
      setEmailLoading(false);
    }
  };

  // ──────────── "Continue as last" smart hand-off ────────────
  const continueAsLast = () => {
    if (!lastAuth) return;
    if (!agreed) setAgreed(true);
    try {
      sessionStorage.setItem('shopperden_consent', JSON.stringify({
        ...consentMeta(), country: cc,
      }));
    } catch (_e) { /* ignore */ }

    if (lastAuth.method === 'google') {
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      const redirectUrl = window.location.origin + '/auth/callback';
      setGoogleLoading(true);
      trackEvent('auth_google_clicked', '/login', { country: cc, source: 'smart_card' });
      window.location.href = buildGoogleAuthUrl(redirectUrl);
      return;
    }

    if (lastAuth.method === 'whatsapp' && lastAuth.phone) {
      setWaOpen(true);
      setPhone(lastAuth.phone);
      (async () => {
        setWaLoading(true);
        trackEvent('auth_whatsapp_request_otp', '/login', { country: cc, source: 'smart_card' });
        try {
          await axios.post(`${API_URL}/api/auth/whatsapp/request-otp`, { phone: lastAuth.phone });
          setOtpStage('code');
          setWaCountdown(900);
          toast.success('Code sent on WhatsApp.');
        } catch (err) {
          const msg = err?.response?.data?.detail || 'Could not send code. Please try Google or email.';
          toast.error(msg);
        } finally {
          setWaLoading(false);
        }
      })();
      return;
    }

    if (lastAuth.method === 'email' && lastAuth.email) {
      setEmailOpen(true);
      setEmail(lastAuth.email);
    }
  };

  // ──────────── UI ────────────
  return (
    <div className="w-full max-w-md mx-auto" data-testid="unified-auth-panel">
      {/* Local keyframes for the blinking compliance house */}
      <style>{`
        @keyframes authHouseBlink {
          0%, 100% { opacity: 1;   transform: scale(1);    }
          50%       { opacity: 0.35; transform: scale(0.92); }
        }
        .auth-house-blink {
          animation: authHouseBlink 1s ease-in-out infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .auth-house-blink { animation: none; }
        }
      `}</style>

      <div className="mb-6 text-center">
        <h1 className="font-['Playfair_Display'] text-3xl text-[#0F172A] mb-2">
          {mode === 'login' ? 'Welcome back' : step === 'role' ? 'Join Shopperden' : 'Almost there'}
        </h1>
        <p className="text-sm text-slate-500">
          {mode === 'login'
            ? 'Sign in to your dashboard'
            : step === 'role'
              ? 'First, tell us how you’ll use Shopperden — we’ll tailor your dashboard.'
              : 'Pick the fastest sign-up — no password needed.'}
        </p>
      </div>

      {/* STEP 1 — Role picker (register only). Visual card grid. */}
      {mode === 'register' && step === 'role' && (
        <RolePicker
          role={role}
          onRoleChange={setRole}
          onContinue={() => setStep('auth')}
          countryCode={cc}
        />
      )}

      {mode === 'register' && step === 'auth' && (
        <RolePickerBackLink role={role} onBack={() => setStep('role')} />
      )}

      {/* STEP 2 — Compliance + auth options. Visible: always for login,
          only after role pick for register. */}
      {(mode === 'login' || step === 'auth') && (
        <>
          <ComplianceBlock
            agreed={agreed}
            onToggle={setAgreed}
            countryName={countryName}
            regime={regime}
            disclaimer={disclaimer}
          />

          <LastAuthCard
            lastAuth={lastAuth}
            onContinue={continueAsLast}
            onClear={clearLastAuth}
            disabled={googleLoading || waLoading || emailLoading}
          />

          <OAuthButtons
            onGoogleClick={handleGoogle}
            onWhatsAppClick={() => setWaOpen(true)}
            googleLoading={googleLoading}
            showWhatsAppButton={!waOpen}
          />

          <WhatsAppOtpPanel
            open={waOpen}
            onCancel={() => setWaOpen(false)}
            otpStage={otpStage}
            phone={phone}
            onPhoneChange={setPhone}
            otp={otp}
            onOtpChange={setOtp}
            role={role}
            onRoleChange={setRole}
            waCountdown={waCountdown}
            waLoading={waLoading}
            onRequestOtp={requestOtp}
            onVerifyOtp={verifyOtp}
            onChangeNumber={() => setOtpStage('phone')}
          />

          <EmailPasswordForm
            mode={mode}
            open={emailOpen}
            onToggle={setEmailOpen}
            email={email}
            onEmailChange={setEmail}
            password={password}
            onPasswordChange={setPassword}
            showPassword={showPassword}
            onShowPasswordToggle={() => setShowPassword((v) => !v)}
            role={role}
            onRoleChange={setRole}
            loading={emailLoading}
            onSubmit={handleEmail}
          />
        </>
      )}

      <p className="text-xs text-slate-500 text-center mt-4">
        {mode === 'login' ? "New to Shopperden? " : 'Already have an account? '}
        <Link to={mode === 'login' ? '/register' : '/login'} className="text-[#E07A3A] font-semibold hover:underline">
          {mode === 'login' ? 'Create an account' : 'Sign in'}
        </Link>
      </p>
    </div>
  );
};

export default UnifiedAuthPanel;
// Re-export for any legacy importers that may have grabbed ROLE_CARDS from here.
export { ROLE_CARDS };
